/**
 * Google Analytics Helper Library
 *
 * Provides type-safe wrapper functions for Google Analytics 4 (GA4)
 *
 * Setup:
 * 1. Create a GA4 property at https://analytics.google.com
 * 2. Copy your measurement ID (G-XXXXXXXXXX)
 * 3. Add to .env: VITE_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 */

// Type definitions for Google Analytics
type GtagCommand = 'config' | 'set' | 'event' | 'js';
type GtagConfig = Record<string, unknown>;
type DataLayerItem = unknown;

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (
      command: GtagCommand,
      targetId: string | Date,
      config?: GtagConfig
    ) => void;
    dataLayer?: DataLayerItem[];
    GA_MEASUREMENT_ID?: string;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || (typeof window !== "undefined" ? window.GA_MEASUREMENT_ID : undefined);

/**
 * Programmatically initialize GA4 after user consent.
 * Call this when the user accepts cookies for the first time in a session.
 */
export const initAnalytics = (): void => {
  if (!GA_MEASUREMENT_ID || typeof window === "undefined") return;
  if (window.gtag) return; // already initialized

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () { window.dataLayer!.push(arguments); } as unknown as typeof window.gtag;
  window.gtag!("js", new Date());
  window.gtag!("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

/**
 * Check if Google Analytics is initialized and available
 */
export const isAnalyticsAvailable = (): boolean => {
  return !!(GA_MEASUREMENT_ID && typeof window !== "undefined" && window.gtag);
};

/**
 * Track a page view
 * Automatically called on route changes
 */
export const trackPageView = (url: string, title?: string): void => {
  if (!isAnalyticsAvailable()) {
  if (import.meta.env.DEV) {
      console.log("[Analytics] Page view not tracked (GA not initialized):", url);
    }
    return;
  }

  window.gtag!("event", "page_view", {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.href,
  });

  if (import.meta.env.DEV) {
    console.log("[Analytics] Page view tracked:", url);
  }
};

/**
 * Track a custom event
 *
 * @param eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
): void => {
  if (!isAnalyticsAvailable()) {
  if (import.meta.env.DEV) {
      console.log("[Analytics] Event not tracked (GA not initialized):", eventName, eventParams);
    }
    return;
  }

  window.gtag!("event", eventName, eventParams);

  if (import.meta.env.DEV) {
    console.log("[Analytics] Event tracked:", eventName, eventParams);
  }
};

/**
 * Track button or link clicks
 */
export const trackClick = (
  elementName: string,
  additionalParams?: Record<string, unknown>
): void => {
  trackEvent("click", {
    element_name: elementName,
    ...additionalParams,
  });
};

/**
 * Track CTA (Call-to-Action) button clicks
 */
export const trackCTAClick = (
  ctaName: string,
  ctaLocation: string,
  ctaDestination?: string
): void => {
  trackEvent("cta_click", {
    cta_name: ctaName,
    cta_location: ctaLocation,
    cta_destination: ctaDestination,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (
  formName: string,
  formLocation: string,
  success: boolean = true
): void => {
  trackEvent("form_submit", {
    form_name: formName,
    form_location: formLocation,
    form_success: success,
  });
};

/**
 * Track form errors
 */
export const trackFormError = (
  formName: string,
  errorMessage: string
): void => {
  trackEvent("form_error", {
    form_name: formName,
    error_message: errorMessage,
  });
};

/**
 * Track pricing interactions
 */
export const trackPricingInteraction = (
  planName: string,
  action: "view" | "select" | "compare",
  planPrice?: string
): void => {
  trackEvent("pricing_interaction", {
    plan_name: planName,
    interaction_type: action,
    plan_price: planPrice,
  });
};

/**
 * Track trial signups
 */
export const trackTrialSignup = (
  planName: string,
  planType: string
): void => {
  trackEvent("trial_signup", {
    plan_name: planName,
    plan_type: planType,
    value: 0, // Can be updated with actual value
  });
};

/**
 * Track video plays
 */
export const trackVideoPlay = (
  videoName: string,
  videoLocation: string
): void => {
  trackEvent("video_play", {
    video_name: videoName,
    video_location: videoLocation,
  });
};

/**
 * Track downloads
 */
export const trackDownload = (
  fileName: string,
  fileType: string,
  downloadLocation: string
): void => {
  trackEvent("file_download", {
    file_name: fileName,
    file_type: fileType,
    download_location: downloadLocation,
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm: string, searchLocation: string): void => {
  trackEvent("search", {
    search_term: searchTerm,
    search_location: searchLocation,
  });
};

/**
 * Track outbound link clicks
 */
export const trackOutboundLink = (url: string, linkText?: string): void => {
  trackEvent("outbound_link_click", {
    link_url: url,
    link_text: linkText,
  });
};

/**
 * Track user engagement time
 */
export const trackEngagement = (
  engagementType: string,
  duration?: number
): void => {
  trackEvent("user_engagement", {
    engagement_type: engagementType,
    engagement_time_msec: duration,
  });
};

/**
 * Track errors that occur in the application
 */
export const trackError = (
  errorMessage: string,
  errorLocation: string,
  errorType?: string
): void => {
  trackEvent("error", {
    error_message: errorMessage,
    error_location: errorLocation,
    error_type: errorType || "application_error",
    fatal: false,
  });
};

/**
 * Set user properties
 * Call this after user authentication or identification
 */
export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (!isAnalyticsAvailable()) {
    if (import.meta.env.DEV) {
      console.debug("[Analytics] User properties not set (GA not initialized)");
    }
    return;
  }

  window.gtag!("set", "user_properties", properties);

  if (import.meta.env.DEV) {
    console.debug("[Analytics] User properties set:", properties);
  }
};

/**
 * Set user ID for cross-device tracking
 */
export const setUserId = (userId: string): void => {
  if (!isAnalyticsAvailable()) {
    if (import.meta.env.DEV) {
      console.debug("[Analytics] User ID not set (GA not initialized)");
    }
    return;
  }

  window.gtag!("set", GA_MEASUREMENT_ID!, {
    user_id: userId,
  });

  if (import.meta.env.DEV) {
    console.debug("[Analytics] User ID set:", userId);
  }
};

/**
 * Clear user ID (on logout)
 */
export const clearUserId = (): void => {
  if (!isAnalyticsAvailable()) {
    return;
  }

  window.gtag!("set", GA_MEASUREMENT_ID!, {
    user_id: null,
  });

  if (import.meta.env.DEV) {
    console.debug("[Analytics] User ID cleared");
  }
};

// Export all tracking functions
export default {
  isAnalyticsAvailable,
  trackPageView,
  trackEvent,
  trackClick,
  trackCTAClick,
  trackFormSubmit,
  trackFormError,
  trackPricingInteraction,
  trackTrialSignup,
  trackVideoPlay,
  trackDownload,
  trackSearch,
  trackOutboundLink,
  trackEngagement,
  trackError,
  setUserProperties,
  setUserId,
  clearUserId,
};
